import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * DualValuationSparkline — Évolution Brute vs Nette sur 90 jours.
 *
 * V23.15.16 — Sparkline SVG natif (zéro dépendance) qui empile :
 *  - Une zone GRISE pour la brute (theoretical ceiling)
 *  - Une zone ORANGE pour la nette (capturable)
 *  - L'écart entre les deux = la part érodée par les frictions
 *
 * V23.15.28 — Ajout des annotations événementielles : points cliquables sur
 * la courbe nette qui révèlent le « pourquoi » des sauts (ex : "Le 24/02,
 * +12 M€ net : LTV calibré"). Cf. agent_valuation.detect_history_events().
 *
 * Gamifie la résolution de frictions : voir la nette monter au fil du temps
 * quand des frictions sont résolues.
 */
export default function DualValuationSparkline({
  scope = "origination",
  days = 90,
  width = 380,
  height = 56,
  testId = "valuation-sparkline",
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [hoveredEvent, setHoveredEvent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/workspace/valuations/${scope}/history?days=${days}`)
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => { cancelled = true; };
  }, [scope, days]);

  if (error || !data) {
    return (
      <div className="text-[10px] text-zinc-600 italic" data-testid={`${testId}-empty`}>
        Historique indisponible.
      </div>
    );
  }

  const series = data.series || [];
  const events = Array.isArray(data.events) ? data.events : [];
  const maxBrute = Math.max(1, data.max_brute || 0);
  const n = series.length;
  const stepX = n > 1 ? width / (n - 1) : width;

  // Lignes brute + nette en path
  const path = (key) => series
    .map((p, i) => {
      const y = height - (Number(p[key] || 0) / maxBrute) * (height - 2);
      const x = i * stepX;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");

  const bruteArea = path("brute_mEUR") + ` L ${width} ${height} L 0 ${height} Z`;
  const netteArea = path("nette_mEUR") + ` L ${width} ${height} L 0 ${height} Z`;

  // Evolution sur la période
  const first = data.first_value || {};
  const last = data.last_value || {};
  const deltaNette = (last.nette_mEUR || 0) - (first.nette_mEUR || 0);
  const deltaSign = deltaNette > 0 ? "+" : deltaNette < 0 ? "" : "";
  const deltaColor = deltaNette > 0 ? "text-emerald-400" : deltaNette < 0 ? "text-[#E24B4A]" : "text-zinc-500";

  // V23.15.28 — Couleur du dot selon severity
  const dotFill = (sev) => sev === "positive" ? "#10B981" : sev === "negative" ? "#E24B4A" : "#A1A1AA";
  // Position Y de chaque event = position sur la courbe nette
  const dotPosForEvent = (ev) => {
    const idx = Math.max(0, Math.min(n - 1, ev.index || 0));
    const point = series[idx] || {};
    const y = height - (Number(point.nette_mEUR || 0) / maxBrute) * (height - 2);
    const x = idx * stepX;
    return { x, y };
  };

  const fmtDate = (s) => {
    if (!s) return "";
    const [, m, d] = s.split("-");
    return d && m ? `${d}/${m}` : s;
  };

  return (
    <div className="mt-3 pt-3 border-t border-white/5 relative" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">
          Évolution Brute vs Nette · {days}j
          {events.length > 0 && (
            <span className="ml-2 text-[#EF9F27]" data-testid={`${testId}-events-count`}>
              · {events.length} événement{events.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className={`text-[11px] font-medium ${deltaColor}`} data-testid={`${testId}-delta`}>
          {deltaSign}{deltaNette.toFixed(1)} M€ net sur la période
        </div>
      </div>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="overflow-visible"
      >
        <path d={bruteArea} fill="#71717A" opacity="0.18" />
        <path d={netteArea} fill="#EF9F27" opacity="0.4" />
        <path d={path("brute_mEUR")} stroke="#A1A1AA" strokeWidth="1" fill="none" opacity="0.6" />
        <path d={path("nette_mEUR")} stroke="#EF9F27" strokeWidth="1.5" fill="none" />

        {/* V23.15.28 — annotations event dots */}
        {events.map((ev, idx) => {
          const { x, y } = dotPosForEvent(ev);
          const isHovered = hoveredEvent === idx;
          return (
            <g
              key={`ev-${idx}`}
              transform={`translate(${x.toFixed(2)}, ${y.toFixed(2)})`}
              onMouseEnter={() => setHoveredEvent(idx)}
              onMouseLeave={() => setHoveredEvent((cur) => (cur === idx ? null : cur))}
              style={{ cursor: "pointer" }}
              data-testid={`${testId}-event-dot-${idx}`}
            >
              <circle r={isHovered ? 4.5 : 3} fill={dotFill(ev.severity)} stroke="#0A0A0A" strokeWidth="1.2" />
              {isHovered && (
                <circle r="8" fill={dotFill(ev.severity)} opacity="0.18" />
              )}
            </g>
          );
        })}
      </svg>

      {/* V23.15.28 — Tooltip de l'event survolé */}
      {hoveredEvent !== null && events[hoveredEvent] && (
        <EventTooltip
          event={events[hoveredEvent]}
          fmtDate={fmtDate}
          testId={`${testId}-event-tooltip`}
        />
      )}

      <div className="flex items-center gap-4 mt-1 text-[10px] text-zinc-500">
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-zinc-400 opacity-50" /> Brute (plafond théorique)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-[#EF9F27]" /> Nette (capturable)
        </span>
        {events.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="w-2 h-2 rounded-full bg-[#E24B4A] -ml-0.5" />
            Événements
          </span>
        )}
      </div>
    </div>
  );
}

function EventTooltip({ event, fmtDate, testId }) {
  const sign = event.delta_nette_mEUR > 0 ? "+" : "";
  const deltaColor =
    event.delta_nette_mEUR > 0 ? "text-emerald-400" :
    event.delta_nette_mEUR < 0 ? "text-[#E24B4A]" : "text-zinc-400";
  return (
    <div
      className="absolute top-0 right-0 mt-6 z-20 px-2.5 py-1.5 rounded-md bg-[#0F0F11] border border-white/15 shadow-lg max-w-xs pointer-events-none"
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">
        {fmtDate(event.date)}
      </div>
      <div className="text-[12px] text-zinc-100 font-medium leading-tight">
        {event.label}
      </div>
      {event.delta_nette_mEUR !== 0 && (
        <div className={`text-[11px] mt-0.5 ${deltaColor}`}>
          {sign}{event.delta_nette_mEUR.toFixed(1)} M€ net
        </div>
      )}
    </div>
  );
}
